import { useContext, useEffect, useState } from 'react';
import classNames from 'classnames';
import { REPLY } from '../../../types/CommentTypes';
import { Comment } from './Comment';
import { ReplyEditor } from './ReplyEditor';
import styled from 'foundations-theming/utils';
import { View } from 'foundations-components/components/View';
import { Flex } from 'foundations-components/components/Flex';
import { Box } from 'foundations-components/transitional/components/Box';
import { Button } from 'foundations-components/transitional/components/Button';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useLocalStorageState } from '../../../utils/useLocalStorageState';
import { getReplyCommentStorageKey } from '../../../constants/LocalStorageConstants';
import { CommentsContext } from '../../../contexts/CommentsContext';
import { AVATAR_SIZE_PX } from '../../../constants/AvatarSizes';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const AVATAR_WIDTH = 24;
const AVATAR_HEIGHT = 24;
const LINE_THICKNESS = 2;

// These styles are for drawing lines between comment avatars in a thread or when reply box is open
const StyledDivContainer = styled.div.withConfig({
  displayName: "CommentThread__StyledDivContainer"
})(["&.thread .comment:first-child::after,&.no-thread.reply-editor-open .comment:first-child::after{border-bottom:", " solid ", ";width:calc(", "px + 6px);height:calc(100% - ", "px / 2);border-radius:", ";}&.thread .comment::after,&.thread.reply-editor-open .comment:last-child::after,&.no-thread.reply-editor-open .comment::after{transform:translateX(-", "px);height:calc(100% - ", "px);margin-top:", ";width:1px;left:calc(13px + ", "px / 2);position:absolute;content:'';background:transparent;border-left:", " solid ", ";}&.thread .comment:last-of-type::after{content:none;}"], ({
  theme
}) => theme.borderWidth['100'], ({
  theme
}) => theme.color['border-core-default'], AVATAR_WIDTH, AVATAR_HEIGHT, ({
  theme
}) => `${theme.borderRadius['0']} ${theme.borderRadius['0']} ${theme.borderRadius['0']} ${theme.borderRadius['400']}`, LINE_THICKNESS, AVATAR_HEIGHT, ({
  theme
}) => theme.space['600'], AVATAR_WIDTH, ({
  theme
}) => theme.borderWidth['100'], ({
  theme
}) => theme.color['border-core-default']);

// Anchor is the object that a comment is referencing

export const CommentThread = props => {
  const {
    commentThread,
    anchor,
    variant,
    isPaginationEnabled = false,
    showRepliesExpanded,
    onToggleReplies,
    isReplyItem = false,
    isLastReplyInThread = false
  } = props;
  const {
    shouldDisableReplying,
    avatarConfig: {
      size: avatarSize,
      flushInline
    },
    renderToggleRepliesButton
  } = useContext(CommentsContext);
  const parentCommentId = commentThread.comment.type === REPLY && commentThread.comment.parentId ? commentThread.comment.parentId : commentThread.comment.id;
  const replyCommentStorageKey = getReplyCommentStorageKey({
    objectType: commentThread.comment.objectType,
    objectId: commentThread.comment.objectId,
    parentCommentId
  });
  const [replyCommentDraft] = useLocalStorageState({
    key: replyCommentStorageKey,
    initialValue: undefined
  });
  const [showReplyEditor, setShowReplyEditor] = useState(false);
  const [internalShowReplies, setInternalShowReplies] = useState(!isPaginationEnabled);
  useEffect(() => {
    if (!isReplyItem && replyCommentDraft !== undefined) {
      setShowReplyEditor(true);
    }
  }, [replyCommentDraft, isReplyItem]);
  const showReplies = isPaginationEnabled ? showRepliesExpanded !== null && showRepliesExpanded !== void 0 ? showRepliesExpanded : internalShowReplies : true;
  const handleToggleReplies = () => {
    if (isPaginationEnabled && onToggleReplies) {
      onToggleReplies();
    } else {
      setInternalShowReplies(prev => !prev);
    }
  };
  const hasReplies = commentThread.replies.length > 0;
  const replyEditorComponent = showReplyEditor && /*#__PURE__*/_jsx(View, {
    paddingInlineStart: "1000",
    children: /*#__PURE__*/_jsx(ReplyEditor, {
      repliedToComment: commentThread.comment,
      onCancelCallback: () => setShowReplyEditor(false),
      onSubmitCallback: () => setShowReplyEditor(false)
    })
  });
  if (isReplyItem) {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Comment, {
        commentData: commentThread.comment,
        showReplyEditor: showReplyEditor,
        updateShowReplyEditor: setShowReplyEditor,
        showReplyLink: isLastReplyInThread,
        anchor: anchor,
        variant: variant
      }), replyEditorComponent]
    });
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(StyledDivContainer, {
      "data-test-id": "comments-threadContainer",
      className: classNames('comment-thread-container', (!showReplies || commentThread.replies.length === 0) && 'no-thread', showReplies && commentThread.replies.length > 0 && "thread", showReplyEditor && 'reply-editor-open'),
      children: [/*#__PURE__*/_jsx(Comment, {
        commentData: commentThread.comment,
        showReplyEditor: showReplyEditor,
        updateShowReplyEditor: setShowReplyEditor,
        showReplyLink: commentThread.replies.length === 0,
        anchor: anchor,
        variant: variant
      }), isPaginationEnabled && hasReplies && !showReplies && !shouldDisableReplying && /*#__PURE__*/_jsxs(Flex, {
        paddingInlineStart: flushInline ? '200' : '300',
        alignItems: "center",
        children: [/*#__PURE__*/_jsx(Box, {
          grow: 0,
          shrink: 0,
          basis: AVATAR_SIZE_PX[avatarSize],
          marginInlineEnd: flushInline ? '200' : '300'
        }), renderToggleRepliesButton ? renderToggleRepliesButton({
          onClick: handleToggleReplies,
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: 'objectCommentsThread.comments.showReplies',
            options: {
              count: commentThread.replies.length
            }
          })
        }) : /*#__PURE__*/_jsx(Button, {
          onClick: handleToggleReplies,
          use: "link",
          size: "xs",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: 'objectCommentsThread.comments.showReplies',
            options: {
              count: commentThread.replies.length
            }
          })
        })]
      }), showReplies && !isPaginationEnabled && commentThread.replies.map((comment, index) => /*#__PURE__*/_jsx(Comment, {
        commentData: comment,
        showReplyEditor: showReplyEditor,
        updateShowReplyEditor: setShowReplyEditor,
        showReplyLink: index === commentThread.replies.length - 1,
        anchor: anchor,
        variant: variant
      }, comment.id)), isPaginationEnabled && hasReplies && showReplies && !shouldDisableReplying && /*#__PURE__*/_jsxs(Flex, {
        paddingInlineStart: flushInline ? '200' : '300',
        alignItems: "center",
        children: [/*#__PURE__*/_jsx(Box, {
          grow: 0,
          shrink: 0,
          basis: AVATAR_SIZE_PX[avatarSize],
          marginInlineEnd: flushInline ? '200' : '300'
        }), renderToggleRepliesButton ? renderToggleRepliesButton({
          onClick: handleToggleReplies,
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "objectCommentsThread.comments.hideReplies"
          })
        }) : /*#__PURE__*/_jsx(Button, {
          onClick: handleToggleReplies,
          use: "link",
          size: "xs",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "objectCommentsThread.comments.hideReplies"
          })
        })]
      })]
    }), replyEditorComponent]
  });
};